// frontend/src/charts/format.js
// Number formatting for charts. Without a presentation ("auto"): compact on
// axes, value labels and KPI tiles (12.9K), full in tooltips and tables
// (12,873.5). A chart's presentation (step 7b-2b, decision D10) can choose
// full / compact / percent / currency and a fixed number of decimals.
// null/undefined/NaN render as an em dash.

const compactFmt = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const fullFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

function isNumber(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

export function compactNumber(v) {
  if (!isNumber(v)) return '—';
  return Math.abs(v) < 1000 ? fullFmt.format(v) : compactFmt.format(v);
}

export function fullNumber(v) {
  if (!isNumber(v)) return '—';
  return fullFmt.format(v);
}

// ---------------------------------------------------------------------------
// Presentation (D10). Keep these lists in step with
// backend/app/semantic/presentation.py: the server validates on save, and
// normalizePresentation applies the same allowlist again here, so a value
// the server would refuse can never reach Intl (an unknown currency throws).
// ---------------------------------------------------------------------------

export const NUMBER_FORMATS = ['auto', 'full', 'compact', 'percent', 'currency'];
export const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'JPY', 'CNY', 'INR', 'CHF',
  'SEK', 'NOK', 'DKK', 'MXN', 'BRL', 'ZAR', 'SGD', 'HKD', 'ILS', 'AED',
];
export const MAX_DECIMALS = 4;
export const DEFAULT_PRESENTATION = Object.freeze({
  number_format: 'auto', currency: 'USD', decimals: null, value_labels: false,
});

/** Any input (stored JSON, the embed config, builder state) -> a complete, safe presentation. */
export function normalizePresentation(raw) {
  const p = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const fmt = NUMBER_FORMATS.includes(p.number_format) ? p.number_format : 'auto';
  const decimals = Number.isInteger(p.decimals) && p.decimals >= 0 && p.decimals <= MAX_DECIMALS ? p.decimals : null;
  return {
    number_format: fmt,
    currency: CURRENCIES.includes(p.currency) ? p.currency : 'USD',
    decimals,
    value_labels: p.value_labels === true,
  };
}

/** Where a number is shown. Axis ticks, value labels and KPI tiles are short; the rest are exact. */
export const PLACES = ['axis', 'label', 'kpi', 'tooltip', 'cell'];
const SHORT_PLACES = ['axis', 'label', 'kpi'];

const cache = new Map();
function intl(options) {
  const key = JSON.stringify(options);
  let f = cache.get(key);
  if (!f) { f = new Intl.NumberFormat('en-US', options); cache.set(key, f); }
  return f;
}

function fixed(decimals) {
  return decimals == null ? {} : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
}

/**
 * A function (number) -> text for one place on the chart. Non-numbers give
 * '—' (callers that must draw nothing, like value labels, check first).
 */
export function makeFormatter(presentation, place = 'tooltip') {
  const p = normalizePresentation(presentation);
  const short = SHORT_PLACES.includes(place);
  const d = p.decimals;
  let format;

  switch (p.number_format) {
    case 'full':
      format = (v) => intl(d == null ? { maximumFractionDigits: 2 } : fixed(d)).format(v);
      break;
    case 'compact':
      format = (v) => intl({ notation: 'compact', ...(d == null ? { maximumFractionDigits: 1 } : fixed(d)) }).format(v);
      break;
    case 'percent':
      // A ratio: 0.25 shows as 25% (Excel, Power BI and Looker do the same).
      format = (v) => intl({ style: 'percent', ...(d == null ? { maximumFractionDigits: 1 } : fixed(d)) }).format(v);
      break;
    case 'currency': {
      const base = { style: 'currency', currency: p.currency };
      format = short
        // Short money drops the cents: $12.9K, $0 (an explicit minimum keeps every browser in step).
        ? (v) => intl({ ...base, notation: 'compact', ...(d == null ? { minimumFractionDigits: 0, maximumFractionDigits: 1 } : fixed(d)) }).format(v)
        : (v) => intl({ ...base, ...fixed(d) }).format(v);   // the currency's own decimals unless fixed
      break;
    }
    default:   // auto: today's behaviour, with fixed decimals when chosen
      if (d == null) {
        format = short ? compactNumber : fullNumber;
      } else {
        format = short
          ? (v) => (Math.abs(v) < 1000 ? intl(fixed(d)) : intl({ notation: 'compact', ...fixed(d) })).format(v)
          : (v) => intl(fixed(d)).format(v);
      }
  }
  return (v) => (isNumber(v) ? format(v) : '—');
}

// ---------------------------------------------------------------------------
// A measure's own format (13c-4). The Model page stores number | integer |
// percent | currency on a saved measure or a calculated column, and the server
// puts it on the result column (`format`) when the aggregate keeps the unit.
// Under the chart's "Automatic" number format each series follows its own;
// a chart that chose a format applies it to every series, as before.
// ---------------------------------------------------------------------------

export const MEASURE_FORMATS = ['number', 'integer', 'percent', 'currency'];

/** The presentation one measure is drawn with: the chart's, or its own format under "auto". */
export function presentationFor(presentation, format) {
  const p = normalizePresentation(presentation);
  if (p.number_format !== 'auto' || !MEASURE_FORMATS.includes(format)) return p;
  if (format === 'currency') return { ...p, number_format: 'currency' };     // the chart's currency (USD unless chosen)
  if (format === 'percent') return { ...p, number_format: 'percent' };
  if (format === 'integer') return { ...p, decimals: p.decimals == null ? 0 : p.decimals };
  return p;                                                                 // 'number': automatic
}

/** A formatter for one result column (a table or matrix cell, a KPI). */
export function columnFormatter(presentation, col, place = 'cell') {
  return makeFormatter(presentationFor(presentation, col && col.format), place);
}

/**
 * The format of each chart series, from the result columns, following the
 * server's shaping (shape_result): with 0 or 1 dimensions one series per
 * measure in order; pivoted (2+ dimensions) every series is the first measure.
 * Without columns, or past the known measures, null (the chart's own format).
 */
export function seriesFormats(columns, count) {
  const cols = Array.isArray(columns) ? columns : [];
  const measures = cols.filter((c) => c && c.kind === 'measure');
  const pivot = cols.filter((c) => c && c.kind === 'dimension').length >= 2;
  const fmt = (c) => (c && MEASURE_FORMATS.includes(c.format) ? c.format : null);
  return Array.from({ length: count }, (_, i) => fmt(pivot ? measures[0] : measures[i]));
}

/** A table cell: numbers formatted (by the chart's presentation when given), everything else as text. */
export function cellText(v, numberFormat = fullNumber) {
  if (v === null || v === undefined || v === '') return '—';
  if (isNumber(v)) return numberFormat(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

// A dated bucket as the server sends it in a table row: an ISO timestamp (the
// bucket's start) or a plain date. The pieces are read from the string, never
// through a Date, so the label is the bucket's own wall-clock date whatever the
// viewer's time zone (the chart's axis labels are built the same way on the server).
const ISO_DATE_START = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * A dimension cell. A dated bucket is written the way the chart's axis writes
 * it (year 2026, quarter 2026 Q3, month 2026-09, week or day 2026-09-01) instead
 * of the raw 2026-09-01T00:00:00+00:00; everything else as cellText.
 */
export function dimensionText(v, col) {
  const grain = col && col.grain;
  if (grain && typeof v === 'string') {
    const m = ISO_DATE_START.exec(v);
    if (m) {
      const [, y, mo, d] = m;
      if (grain === 'year') return y;
      if (grain === 'quarter') return `${y} Q${Math.floor((Number(mo) - 1) / 3) + 1}`;
      if (grain === 'month') return `${y}-${mo}`;
      if (grain === 'week' || grain === 'day') return `${y}-${mo}-${d}`;
    }
  }
  return cellText(v);
}

const AGG_LABEL = { count: 'Count', count_distinct: 'Distinct', sum: 'Sum of', avg: 'Average', min: 'Min', max: 'Max' };

/** Human label for a result column; mirrors the backend's measure naming. A calculation's column carries its display name as `label` (13c). */
export function columnLabel(col) {
  const field = col.label || (col.field || '').replace(/_/g, ' ');
  if (col.kind !== 'measure') return field || col.alias;
  if (col.aggregation === 'count') return 'Count';
  if (col.aggregation === 'metric') return col.label || String(col.field || col.alias || '').replace(/^_metric_/, '').replace(/_/g, ' ');   // a saved measure is its own name
  return `${AGG_LABEL[col.aggregation] || col.aggregation} ${field}`.trim();
}
